/* PODbySoni quick-add drawer. Added Sep 7, 2026.

   Drives the colour/size sheet whose markup is snippets/pbs-quick-drawer.liquid,
   rendered once per product card. Loaded from sections/pbs-marquee.liquid, which
   is in header-group and therefore on every page, because a product card can be
   rendered by seven different sections.

   WHY THIS FILE EXISTS INSTEAD OF Dawn's assets/quick-add.js
   Dawn's modal fetches the product page and lifts <product-info> out of it. This
   store's product page is sections/pbs-product.liquid, written from scratch, and
   emits no such element, so quick-add.js dies on null.classList with nothing on
   screen. That is the Sep 3, 2026 bug. Everything this file needs is already in
   the page, rendered server side by the snippet, so it never fetches anything.

   THE STANDING WARNING FROM podbysoni-brand.js APPLIES HERE. Do NOT add a
   page-wide MutationObserver to find cards. This file writes to the DOM on every
   swatch tap; a page-wide observer would see its own writes, re-trigger itself
   and lock the tab up hard, which is how this theme was deadlocked before.
   There is no observer here and there does not need to be: the opener is ONE
   delegated click listener on document, so cards that arrive later - a collection
   facet re-render, Dawn's "You may also like" fetch - are picked up for free,
   and each drawer initialises itself the first time it is actually opened.

   ORDER OF BUSINESS, if you are debugging:
     1. open()   moves the drawer to <body>, why is explained there
     2. init()   parses the variant table, binds the option buttons
     3. apply()  the whole of the availability logic lives here
     4. focus    Escape, Tab cycling, and returning focus to the pill
     5. opener   one delegated click; closes the bag / Saved first (step 14e)
     6. add      handing off to Dawn's product-form.js and the cart drawer,
                 then flashing the new bag line (.pbs-just, step 13 owns the CSS)
     Also: the "Size guide" link hands off to window.PBSSizeGuide (step 14d,
     contract C3); the need-size state replays on a submit with no size (14b). */
(function () {
  'use strict';

  /* The drawer currently on screen, the pill that opened it, and the card it
     came out of. Only one can be open at a time, so these are module scoped
     rather than hung off each drawer. */
  var OPEN = null;
  var OPENER = null;
  var HOME = null;

  /* Same constant as snippets/cart-drawer.liquid (`pbs_ship_goal = 3500`, in
     cents). If one changes, change the other: the fine print under the Add
     button and the bag's free-shipping bar must never disagree. */
  var FREE_SHIP = 3500;

  /* ======================================================================
     HELPERS
     ====================================================================== */

  function pick(root, attr) {
    /* The chosen value of an option group, read back off the DOM rather than
       held in a variable. Same contract sections/pbs-product.liquid uses, so
       the two pickers behave identically and can be reasoned about together. */
    var b = root.querySelector('[' + attr + '].is-on');
    return b ? b.getAttribute(attr) : null;
  }

  function setPressed(btn, on) {
    btn.classList.toggle('is-on', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function list(names) {
    /* "L, XL and 2XL". Written out rather than joined with commas because this
       string is read aloud by a screen reader as well as printed. */
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  /* ======================================================================
     1. OPEN AND CLOSE
     ====================================================================== */

  /* WHY THE DRAWER IS MOVED TO <body> AND NOT LEFT WHERE IT WAS RENDERED.

     The snippet renders inside .pbs-card, which it has to: .pbs-grid is a CSS
     grid, so a sibling of the card would claim a grid cell of its own and blow
     a hole in the row.

     But .pbs-card carries `transform: translateY(-9px)` on hover
     (podbysoni-brand.css section 6). A transformed element becomes the
     containing block for `position: fixed` descendants, so a drawer left inside
     the card would be positioned against the CARD, not the viewport: on desktop
     it would slide up inside a 300px square the moment the pointer touched the
     card, which is most of the time, since the pill that opens it is in there.
     Adding `transform: none` while open does not fix it either, because the
     transform is still non-none for the length of its 0.5s transition out.

     Moving it to <body> for the duration is the only thing that is true in
     every case, and it costs one appendChild. It is put back on close so a
     collection facet re-render cannot leave orphaned drawers piling up on the
     body with duplicate ids. */
  function open(drawer, opener) {
    if (OPEN) close(false);

    var st = init(drawer);
    if (!st) return false;

    /* No size is ever preselected (step 14b): a size the shopper did not choose
       would go to print wrong, and made-to-order has no size returns. Reopening
       a drawer must not remember the last visit's size or its need state. */
    if (st.data.hasSize) {
      drawer.querySelectorAll('[data-pbs-qa-size]').forEach(function (b) { setPressed(b, false); });
      if (st.sizeName) st.sizeName.textContent = 'Pick one';
      if (st.sizeBlock) st.sizeBlock.classList.remove('need');
      apply(st, false);
    }

    OPEN = drawer;
    OPENER = opener || null;
    HOME = drawer.parentNode;

    document.body.appendChild(drawer);
    drawer.hidden = false;
    /* Two frames' grace so the browser has laid the sheet out off the bottom
       edge before the class that slides it up is added. One frame is enough in
       Chrome and is not in Safari. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
        /* Focus only once display is on; focus() on display:none is ignored. */
        if (first) first.focus();
      });
    });

    /* Same scroll lock the quiz modal uses. documentElement, not body: Dawn's
       cart drawer owns body.overflow-hidden and the two must not fight. */
    document.documentElement.style.overflow = 'hidden';
    /* CSS lifts the toast above the sheet while this is on. */
    document.documentElement.classList.add('pbs-qa-open');

    document.addEventListener('keydown', onKey);

    /* Focus the first real control rather than the sheet, so a screen reader
       lands on something actionable. The close button is deliberately not it -
       opening a picker and being put on "Close" reads as a dead end. */
    var first = drawer.querySelector('[data-pbs-qa-color], [data-pbs-qa-size]')
      || drawer.querySelector('[data-pbs-qa-close]');
    
    return true;
  }

  /* restoreFocus is false in exactly one case: the add succeeded and Dawn's
     cart drawer is about to trap focus on itself. Pulling focus back to the
     pill first would yank the shopper out of the drawer that just opened. */
  function close(restoreFocus) {
    if (!OPEN) return;

    var drawer = OPEN;
    var opener = OPENER;

    OPEN = null;
    OPENER = null;

    document.removeEventListener('keydown', onKey);
    drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('pbs-qa-open');

    /* Hide and re-home after the slide-down, but do not make correctness wait
       on a transitionend that a background tab will never fire - Chrome freezes
       CSS transitions in tabs that are not in front, which is written up at the
       top of assets/pbs-ux.js. A plain timeout always fires. */
    var home = HOME;
    HOME = null;
    setTimeout(function () {
      /* Re-opened inside the slide-down? Leave it alone. Tapping the pill
         twice in quick succession is a normal thing to do on a phone. */
      if (OPEN === drawer) return;
      drawer.hidden = true;
      if (home && home.isConnected) home.appendChild(drawer);
      else drawer.remove(); /* home was re-rendered (e.g. the bag): do not leave duplicate ids on body */
    }, 320);

    if (restoreFocus !== false && opener && opener.focus) opener.focus();
  }

  /* ======================================================================
     2. KEYBOARD
     Escape closes and hands focus back to the pill. Tab cycles inside the
     sheet. This is a local trap rather than Dawn's global trapFocus() on
     purpose: trapFocus keeps ONE module-level handler map shared with the cart
     drawer, and calling removeTrapFocus() on our close would tear down the trap
     the cart drawer had just installed on itself after an add.
     ====================================================================== */
  function focusable(root) {
    var all = root.querySelectorAll(
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    return Array.prototype.filter.call(all, function (el) {
      return !el.disabled
        && el.getAttribute('aria-hidden') !== 'true'
        && (el.offsetWidth > 0 || el.offsetHeight > 0);
    });
  }

  function onKey(e) {
    if (!OPEN) return;

    /* The size guide is stacked on top of this sheet and owns the keyboard
       (its own window capture listener) while it is open. Do not steal Escape
       or Tab from it. */
    if (window.PBSSizeGuide && typeof window.PBSSizeGuide.isOpen === 'function'
        && window.PBSSizeGuide.isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }

    if (e.key !== 'Tab') return;

    var sheet = OPEN.querySelector('.pbs-qa__sheet');
    var f = focusable(sheet);
    if (!f.length) return;

    var first = f[0];
    var last = f[f.length - 1];
    var here = document.activeElement;
    var inside = sheet.contains(here);

    if (e.shiftKey && (here === first || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (here === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ======================================================================
     3. INIT - runs once per drawer, the first time it is opened
     ====================================================================== */
  function init(drawer) {
    if (drawer.pbsQa) return drawer.pbsQa;

    var dataEl = drawer.querySelector('[data-pbs-qa-data]');
    if (!dataEl) return null;

    var data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      /* No table means no picker. Say so once in the console and let the caller
         fall through to the product page link, which is the whole point of the
         pill still being an <a href>. */
      console.error('pbs-quick-add: variant table did not parse', e);
      return null;
    }

    var st = {
      drawer: drawer,
      data: data,
      idInput: drawer.querySelector('[data-pbs-qa-id]'),
      price: drawer.querySelector('[data-pbs-qa-price]'),
      /* Step 22: struck compare-at and "45% off". Only rendered outside the
         shop-metafield sale (that branch keeps its own label). */
      was: drawer.querySelector('[data-pbs-qa-was]'),
      off: drawer.querySelector('[data-pbs-pct-off]'),
      /* Store-wide sale (shop.metafields.pbs.sale). Absent unless the sale was
         live when the card rendered. */
      sale: drawer.querySelector('.pbs-qa__sale[data-pbs-sale]'),
      saleNow: drawer.querySelector('[data-pbs-qa-sale-now]'),
      saleWas: drawer.querySelector('[data-pbs-qa-sale-was]'),
      label: drawer.querySelector('[data-pbs-qa-label]'),
      note: drawer.querySelector('[data-pbs-qa-note]'),
      add: drawer.querySelector('.pbs-qa__add'),
      img: drawer.querySelector('[data-pbs-qa-img]'),
      colorName: drawer.querySelector('[data-pbs-qa-colorname]'),
      form: drawer.querySelector('form'),
      fine: drawer.querySelector('[data-pbs-qa-fine]'),
      sizeBlock: drawer.querySelector('[data-pbs-qa-sizeblock]'),
      sizeName: drawer.querySelector('[data-pbs-qa-sizename]')
    };

    drawer.pbsQa = st;

    bind(st, 'data-pbs-qa-color', true);
    bind(st, 'data-pbs-qa-size', false);

    /* Size guide hand-off (step 14d, contract C3). open() returns a boolean
       synchronously: false on desktop, with the sheet missing or for an unknown
       garment. Only a true return cancels the click, so otherwise the link
       navigates to the product page as it always did. */
    drawer.querySelectorAll('.pbs-qa__guide').forEach(function (link) {
      link.addEventListener('click', function (e) {
        if (!window.PBSSizeGuide || typeof window.PBSSizeGuide.open !== 'function') return;
        var opened = window.PBSSizeGuide.open({
          garment: link.dataset.pbsGarment,
          size: pick(st.drawer, 'data-pbs-qa-size'),
          trigger: link,
          stack: true,
          desktop: true,
          onPick: function (s) {
            var chips = st.drawer.querySelectorAll('[data-pbs-qa-size]');
            for (var i = 0; i < chips.length; i++) {
              var c = chips[i];
              if (c.getAttribute('data-pbs-qa-size') !== s) continue;
              if (c.classList.contains('is-off') || c.getAttribute('aria-disabled') === 'true') return;
              chooseSize(st, c);
              c.focus();
              return;
            }
          }
        });
        if (opened) e.preventDefault();
      });
    });

    drawer.querySelectorAll('[data-pbs-qa-close]').forEach(function (b) {
      b.addEventListener('click', function () { close(true); });
    });

    var scrim = drawer.querySelector('[data-pbs-qa-scrim]');
    if (scrim) scrim.addEventListener('click', function () { close(true); });

    /* A HARD stop on submitting a combination that has no variant.

       aria-disabled on its own is enough for Dawn's product-form.js, which
       checks it and returns. It is NOT enough if product-form.js failed to load
       on this template, in which case the form is a plain HTML form and would
       POST the last good variant id while the shopper is looking at a different
       colour - the silent wrong-item-in-cart failure. Capture phase, so this
       runs before product-form.js's own submit handler. */
    if (st.form) {
      st.form.addEventListener('submit', function (e) {
        /* Sold out / unavailable button: just stop, no "pick a size" prompt. */
        if (st.add.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        /* No size chosen on a product that has sizes: not an error state, a
           prompt. Same capture-phase stop, then the need state (step 14b). */
        if (st.data.hasSize && !pick(st.drawer, 'data-pbs-qa-size')) {
          e.preventDefault();
          e.stopImmediatePropagation();
          needSize(st);
          return;
        }
        var v = match(st);
        if (!v || !v.a) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
    }

    apply(st, false);
    return st;
  }

  function bind(st, attr, movesImage) {
    st.drawer.querySelectorAll('[' + attr + ']').forEach(function (b) {
      b.addEventListener('click', function () {
        /* A struck-through option is still focusable and still announced, so a
           screen reader user can find out that Banana has no S. It just does
           not become the selection. */
        if (b.getAttribute('aria-disabled') === 'true') return;

        if (attr === 'data-pbs-qa-size') {
          chooseSize(st, b);
          return;
        }

        st.drawer.querySelectorAll('[' + attr + ']').forEach(function (o) {
          setPressed(o, o === b);
        });
        apply(st, movesImage);
      });
    });
  }

  /* One path for a size tap and for the size guide's "Choose size X". Writes the
     size into the label, clears the need state and re-runs apply(), which also
     clears the "Pick a size" note. */
  function chooseSize(st, b) {
    st.drawer.querySelectorAll('[data-pbs-qa-size]').forEach(function (o) {
      setPressed(o, o === b);
    });
    if (st.sizeName) st.sizeName.textContent = b.getAttribute('data-pbs-qa-size');
    if (st.sizeBlock) st.sizeBlock.classList.remove('need');
    apply(st, false);
  }

  /* The need-size state: replay the CSS animation (remove, force a reflow,
     re-add), bring the size block into view, toast, announce, and put focus on
     the first size that can actually be bought. */
  function needSize(st) {
    var el = st.sizeBlock;
    if (el) {
      el.classList.remove('need');
      void el.offsetWidth;
      el.classList.add('need');
      if (el.scrollIntoView) {
        el.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }
    if (typeof window.pbsToast === 'function') window.pbsToast('Pick a size');
    say(st, 'Pick a size');

    var chips = st.drawer.querySelectorAll('[data-pbs-qa-size]');
    for (var i = 0; i < chips.length; i++) {
      if (!chips[i].classList.contains('is-off')) { chips[i].focus(); break; }
    }
  }

  /* ======================================================================
     4. VARIANT MATCHING AND AVAILABILITY

     Matching is on the option NAME's index, worked out in Liquid and passed in
     as ci / si, never on option1 / option2. Colour is option 1 on the tees and
     option 2 on the sweatshirts in this catalogue. Getting it wrong does not
     throw, it puts the wrong garment in someone's cart.
     ====================================================================== */
  function match(st, color, size) {
    var d = st.data;
    if (color === undefined) color = pick(st.drawer, 'data-pbs-qa-color');
    if (size === undefined) size = pick(st.drawer, 'data-pbs-qa-size');

    for (var i = 0; i < d.variants.length; i++) {
      var v = d.variants[i];
      if (d.hasColor && v.o[d.ci] !== color) continue;
      if (d.hasSize && v.o[d.si] !== size) continue;
      return v;
    }
    return null;
  }

  /* Every size this colour can actually be bought in, in the order the option
     lists them, so the note reads "Ivory comes in L, XL and 2XL". */
  function sizesFor(st, color) {
    var d = st.data;
    var out = [];
    st.drawer.querySelectorAll('[data-pbs-qa-size]').forEach(function (b) {
      var s = b.getAttribute('data-pbs-qa-size');
      var v = match(st, color, s);
      if (v && v.a) out.push(s);
    });
    return out;
  }

  function apply(st, movesImage) {
    var d = st.data;
    var color = d.hasColor ? pick(st.drawer, 'data-pbs-qa-color') : null;
    var size = d.hasSize ? pick(st.drawer, 'data-pbs-qa-size') : null;

    /* ---- colour swatches ----
       A colour is only struck out when it cannot be bought in ANY size. It is
       deliberately NOT cross-disabled against the size in hand: doing that
       would let a shopper who picked S paint themselves into a corner where
       most of the colours look discontinued. The size row carries the
       narrower truth. */
    if (d.hasColor) {
      st.drawer.querySelectorAll('[data-pbs-qa-color]').forEach(function (b) {
        var c = b.getAttribute('data-pbs-qa-color');
        var any = false;
        for (var i = 0; i < d.variants.length; i++) {
          if (d.variants[i].o[d.ci] === c && d.variants[i].a) { any = true; break; }
        }
        b.classList.toggle('is-off', !any);
        if (any) b.removeAttribute('aria-disabled');
        else b.setAttribute('aria-disabled', 'true');
      });

      if (st.colorName && color) st.colorName.textContent = color;
    }

    /* ---- size chips ----
       Struck out when there is no variant for this colour and size at all
       (a real gap in the catalogue - five of these exist), or when the variant
       exists but is sold out. Both look the same to the shopper; the two are
       told apart in the note under the button, because "we never made it" and
       "it has gone" are different things and only one of them might come back. */
    if (d.hasSize) {
      st.drawer.querySelectorAll('[data-pbs-qa-size]').forEach(function (b) {
        var s = b.getAttribute('data-pbs-qa-size');
        var v = match(st, color, s);
        var ok = !!(v && v.a);
        b.classList.toggle('is-off', !ok);
        if (ok) b.removeAttribute('aria-disabled');
        else b.setAttribute('aria-disabled', 'true');
      });
    }

    /* ---- image ----
       Only a COLOUR change moves the photo. Changing size must not, or picking
       a size would yank the shopper back to the default colourway's picture -
       same rule as the product page gallery. */
    if (movesImage && st.img && color && d.images && d.images[color]) {
      st.img.src = d.images[color];
    }

    /* ---- colour chosen, no size yet ----
       The button stays enabled-looking and shows the price of the first
       variant of this colour that can be bought, so the shopper sees a price
       and pressing it gets the "Pick a size" prompt rather than a dead button.
       [name=id] stays EMPTY: the submit handler stops the form before it could
       post. "Not available" below is only for a size that IS chosen. */
    if (d.hasSize && !size) {
      var fv = null;
      for (var j = 0; j < d.variants.length; j++) {
        var cv = d.variants[j];
        if (d.hasColor && cv.o[d.ci] !== color) continue;
        if (cv.a) { fv = cv; break; }
      }
      st.idInput.value = '';
      if (!fv) {
        st.add.setAttribute('aria-disabled', 'true');
        st.label.textContent = 'Sold out';
        say(st, (color || 'This product') + ' has sold out.');
        return;
      }
      var liveFv = showPrice(st, fv);
      st.add.removeAttribute('aria-disabled');
      st.label.textContent = 'Add to bag \u00b7 ' + (liveFv ? fv.s : fv.p);
      say(st, '');
      return;
    }

    /* ---- price, button, note ---- */
    var v = match(st, color, size);

    if (!v) {
      st.idInput.value = '';
      st.add.setAttribute('aria-disabled', 'true');
      st.label.textContent = 'Not available';
      var can = d.hasSize ? sizesFor(st, color) : [];
      say(st, can.length
        ? color + ' is not made in ' + size + '. It comes in ' + list(can) + '.'
        : (color || 'That combination') + ' is not available.');
      return;
    }

    st.idInput.value = v.i;
    var saleLive = showPrice(st, v);

    if (v.a) {
      st.add.removeAttribute('aria-disabled');
      st.label.textContent = 'Add to bag · ' + (saleLive ? v.s : v.p);
      say(st, '');
    } else {
      st.add.setAttribute('aria-disabled', 'true');
      st.label.textContent = 'Sold out';
      var left = d.hasSize ? sizesFor(st, color) : [];
      say(st, left.length
        ? color + ' in ' + size + ' has sold out. Still here in ' + list(left) + '.'
        : 'Sold out in this combination.');
    }
  }

  /* Writes a variant's price into the head and the fine print, and says whether
     the sale price is the one charged. */
  function showPrice(st, v) {
    if (st.price) st.price.textContent = v.p;
    if (st.was && st.off) {
      /* Rounded, like snippets/pbs-pct-off.liquid: 56.98 -> 31.33 reads 45. */
      var off = (v.k && v.k > v.c) ? Math.round((v.k - v.c) * 100 / v.k) : 0;
      st.was.hidden = !off;
      st.off.hidden = !off;
      if (off) {
        st.was.textContent = v.kp;
        st.off.innerHTML = '<b>' + off + '%</b> off';
        /* Step 22 tiers, same 40% line as the Liquid: loud at 40+, quiet below. */
        st.off.classList.toggle('pbs-pct--hot', off >= 40);
        st.off.classList.toggle('pbs-pct--soft', off < 40);
      }
    }
    if (st.saleNow && v.s) {
      st.saleNow.textContent = v.s;
      st.saleWas.textContent = v.p;
    }
    /* The page may be cached past the sale; the guard in pbs-ux.js checks the
       browser clock. Fall back to whether it hid the sale markup. */
    var live = !!(st.sale && v.s &&
      (window.pbsSaleLive ? window.pbsSaleLive(st.sale) : !st.sale.hidden));
    fine(st, v, live);
    return live;
  }

  /* Fine print under the button. Step 22 (Oct 26, 2026): the same line at every
     price, so it always states the threshold and matches the Liquid in
     snippets/pbs-quick-drawer.liquid. Arguments kept so callers need no change. */
  function fine(st, v, live) {
    if (!st.fine) return;
    st.fine.textContent = 'Printed to order \u00b7 Free US shipping over $35';
  }

  function say(st, text) {
    if (!st.note) return;
    st.note.textContent = text;
    st.note.hidden = !text;
  }

  /* ======================================================================
     5. THE OPENER
     One delegated listener, no observer. Cards that arrive after load - a
     collection facet re-render, Dawn's related-products fetch - work with no
     re-init, because nothing was ever bound to the card in the first place.

     GRACEFUL FALLBACK. The pill is a real <a href> to the product page. If this
     file fails to load, throws, or the drawer's variant table will not parse,
     nothing calls preventDefault and the browser simply follows the link, which
     is exactly what the pill did before this drawer existed.
     ====================================================================== */
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    var pill = e.target.closest('[data-pbs-qa-open]');
    if (!pill) return;

    /* Let a modified click through: cmd/ctrl/shift-click and middle-click all
       mean "open the product page", and always have. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var card = pill.closest('[data-pbs-card]');
    var drawer = card && card.querySelector('[data-pbs-qa]');
    if (!drawer) return;

    /* Overlays never stack (step 14e). A pill inside the bag or Saved closes
       that drawer first, or the sheet (z 215) opens underneath Dawn's (1000).
       Closed before open() on purpose: open() sets the scroll lock itself, and
       neither drawer's close touches documentElement.style.overflow. If open()
       then declines, the link navigates away anyway. */
    if (pill.closest('cart-drawer')) {
      var cd = document.querySelector('cart-drawer');
      if (cd && typeof cd.close === 'function') cd.close();
    }
    if (pill.closest('#pbs-saved')) {
      if (window.pbsSavedDrawer && typeof window.pbsSavedDrawer.close === 'function') {
        window.pbsSavedDrawer.close({ handoff: true });
      }
    }

    /* preventDefault only once the drawer has actually opened. */
    if (open(drawer, pill)) e.preventDefault();
  });

  /* ======================================================================
     6. AFTER THE ADD
     Dawn's product-form.js publishes cartUpdate and then hands the response to
     CartDrawer.renderContents, which opens the cart drawer on the next tick.
     Closing here, inside the publish, means our sheet is on its way out before
     the cart drawer starts trapping focus on itself - and we do NOT take focus
     back to the pill, because the cart drawer is about to want it.

     If pubsub is not on the page for some reason the drawer simply stays open
     behind the cart drawer, which is untidy but not broken.
     ====================================================================== */
  /* Flash the new bag line. Dawn's renderContents runs right after the publish,
     so the row does not exist yet: look on the next tick, and once more at
     120ms if it is still missing. Step 13 owns the .pbs-just animation; this
     only applies the class, removing and re-adding it after a reflow when it is
     already there so a second add of the same variant replays. */
  function flashLine(id, retry) {
    setTimeout(function () {
      var row = document.querySelector('#CartDrawer tr.cart-item[data-variant-id="' + id + '"]');
      if (!row) { if (retry) flashLine(id, false); return; }
      if (row.classList.contains('pbs-just')) {
        row.classList.remove('pbs-just');
        void row.offsetWidth;
      }
      row.classList.add('pbs-just');
    }, retry ? 0 : 120);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!OPEN) return;
      if (!event || event.source !== 'product-form') return;

      /* Only close for OUR add. The sheet is modal, so in practice nothing else
         can be submitted while it is up, but comparing the variant id means a
         future non-modal caller cannot make this close the wrong thing.
         Both sides are strings: productVariantId comes off FormData. */
      var st = OPEN.pbsQa;
      if (st && st.idInput && String(event.productVariantId) !== String(st.idInput.value)) return;

      /* Hand the cart drawer the PILL as the element to restore focus to when
         it is dismissed. product-form.js captured document.activeElement at
         submit time, which was our Add to bag button - by the time the cart
         drawer closes, that button is hidden and back inside the card, so
         focus would land nowhere. The pill is where the shopper was. */
      var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart && typeof cart.setActiveElement === 'function' && OPENER) cart.setActiveElement(OPENER);

      /* Captured BEFORE close(): the id input is not touched by close(), but
         this must not depend on that. */
      var addedId = String(event.productVariantId);

      close(false);
      flashLine(addedId, true);
    });
  }
})();
